import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  KeyRound, CreditCard, Sparkles, ExternalLink, AlertTriangle, CheckCircle2,
  UserCog, Download, Trash2, Gift, Copy, ShieldAlert, Activity, RefreshCcw, Users,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_COPY = {
  trialing: { label: "Free trial", tone: "text-[#4e7b5b] bg-[#e8f0e8]" },
  active: { label: "Active subscription", tone: "text-[#4e7b5b] bg-[#e8f0e8]" },
  past_due: { label: "Payment past due", tone: "text-[#c0612f] bg-[#fbeae2]" },
  canceled: { label: "Subscription canceled", tone: "text-[#d94f3d] bg-[#fbeae8]" },
};

function StatusPill({ status }) {
  const meta = STATUS_COPY[status] || { label: status || "—", tone: "text-[#57534e] bg-[#efece6]" };
  return (
    <span data-testid="billing-status-pill" className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] rounded-[3px] ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function BillingPanel({ user, refresh }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const tab = params.get("plan");
  const isOwner = user?.role === "owner";

  const load = async () => {
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Friendly toast when redirected back from a successful (mock) checkout.
    if (params.get("mock") === "1") {
      toast.success(`Subscription activated (${tab || "plan"}) — dev mock mode`);
      refresh?.();
    } else if (params.get("session_id")) {
      toast.success("Thanks! Subscription confirmed.");
      refresh?.();
    } else if (params.get("cancel") === "1") {
      toast.info("Checkout cancelled — you can resubscribe anytime");
    }
  }, []);

  const subscribe = async (plan) => {
    if (!isOwner) return;
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/subscribe", { plan, origin_url: origin });
      if (typeof data?.credits_applied === "number" && data.credits_applied > 0) {
        toast.success(`${data.credits_applied} bonus month${data.credits_applied === 1 ? "" : "s"} applied from your referrals.`);
      }
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data?.mock) {
        toast.info("Stripe portal is only available in live mode. Plan can be cancelled by the owner in Stripe.");
      } else if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-[#8a8476]" data-testid="billing-loading">Loading billing…</div>;
  }

  const monthly = status?.plans?.monthly;
  const yearly = status?.plans?.yearly;
  const trialDays = status?.trial_remaining_days;
  const writeAccess = status?.write_access;
  const cur = status?.subscription_status;

  return (
    <div className="space-y-8" data-testid="billing-panel">
      {/* Current plan card */}
      <div className="bg-white border border-[#e5e2d9] rounded-[4px] p-6" data-testid="billing-current-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Workspace</div>
            <div className="font-display text-2xl text-[#1b3b36] mt-1">{user?.business_name || "—"}</div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <StatusPill status={cur} />
              {status?.complimentary && (
                <span data-testid="billing-complimentary-pill" className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] rounded-[3px] bg-[#fbeae2] text-[#7a3d12]">
                  Complimentary lifetime
                </span>
              )}
              {cur === "trialing" && trialDays != null && !status?.complimentary && (
                <span className="text-xs text-[#57534e]" data-testid="trial-remaining">
                  {trialDays} day{trialDays === 1 ? "" : "s"} of trial remaining
                </span>
              )}
              {status?.subscription_plan && cur === "active" && !status?.complimentary && (
                <span className="text-xs text-[#57534e]" data-testid="active-plan-label">
                  {status.subscription_plan === "monthly" ? "Monthly" : "Annual"} plan
                </span>
              )}
              {!status?.stripe_live && (
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476] bg-[#efece6] rounded-[3px] px-2 py-0.5">
                  Dev mode (mock)
                </span>
              )}
            </div>
          </div>
          {(cur === "active" || cur === "trialing" || cur === "past_due") && (
            <Button
              variant="outline"
              data-testid="billing-portal-button"
              onClick={openPortal}
              disabled={busy || !isOwner}
              className="border-[#e5e2d9] text-[#1b3b36] hover:bg-[#efece6]"
            >
              <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.6} />
              Manage in Stripe
            </Button>
          )}
        </div>

        {!writeAccess && (
          <div className="mt-5 flex items-start gap-3 bg-[#fbeae8] border border-[#f1c5be] text-[#a23a30] rounded-[3px] px-4 py-3 text-sm" data-testid="billing-locked-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
            <div>
              <div className="font-medium">Workspace is read-only</div>
              <div className="text-xs mt-0.5">Choose a plan below to restore writing, exports and team management.</div>
            </div>
          </div>
        )}
        {writeAccess && cur === "trialing" && trialDays != null && trialDays <= 5 && (
          <div className="mt-5 flex items-start gap-3 bg-[#fbeae2] border border-[#e9c4a3] text-[#7a3d12] rounded-[3px] px-4 py-3 text-sm" data-testid="billing-trial-warning">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
            <div>
              <div className="font-medium">Your trial ends in {trialDays} day{trialDays === 1 ? "" : "s"}</div>
              <div className="text-xs mt-0.5">Add a plan now so your workspace stays unlocked.</div>
            </div>
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="grid sm:grid-cols-2 gap-5" data-testid="billing-plans-grid">
        <PlanCard
          name="Monthly"
          price={monthly?.amount}
          unit="/month"
          features={["Unlimited modules", "All compliance exports", "Daily email digest", "Cancel anytime"]}
          highlight={status?.subscription_plan === "monthly"}
          ctaTestId="subscribe-monthly-button"
          busy={busy}
          disabled={!isOwner}
          onClick={() => subscribe("monthly")}
          cta={cur === "active" && status?.subscription_plan === "monthly" ? "Current plan" : "Choose monthly"}
        />
        <PlanCard
          name="Yearly"
          price={yearly?.amount}
          unit="/year"
          subtitle={`Two months free — works out at £${(yearly?.amount / 12).toFixed(2)}/mo`}
          features={["Everything in Monthly", "Lock in 17% saving", "Priority email support", "Cancel anytime"]}
          highlight={status?.subscription_plan === "yearly"}
          ctaTestId="subscribe-yearly-button"
          busy={busy}
          disabled={!isOwner}
          onClick={() => subscribe("yearly")}
          cta={cur === "active" && status?.subscription_plan === "yearly" ? "Current plan" : "Choose yearly"}
          bestValue
        />
      </div>

      {!isOwner && (
        <div className="text-xs text-[#8a8476]" data-testid="billing-owner-only">
          Only the workspace owner can change the subscription.
        </div>
      )}
      <div className="text-xs text-[#8a8476]">
        Need help? Email <a className="underline" href="mailto:info@obnenterprise.co.uk">info@obnenterprise.co.uk</a>.
      </div>
    </div>
  );
}

function PlanCard({ name, price, unit, subtitle, features, highlight, busy, onClick, cta, ctaTestId, bestValue, disabled }) {
  return (
    <div className={`relative bg-white border rounded-[4px] p-6 flex flex-col ${highlight ? "border-[#1b3b36] ring-2 ring-[#1b3b36]/10" : "border-[#e5e2d9]"}`} data-testid={`plan-card-${name.toLowerCase()}`}>
      {bestValue && (
        <div className="absolute -top-2 right-4 text-[9px] uppercase tracking-[0.24em] bg-[#c0612f] text-white px-2 py-0.5 rounded-[3px]">
          Best value
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">{name}</div>
      <div className="flex items-baseline gap-1 mt-3">
        <span className="font-display text-4xl text-[#1b3b36]">£{price ?? "—"}</span>
        <span className="text-sm text-[#8a8476]">{unit}</span>
      </div>
      {subtitle && <div className="text-xs text-[#57534e] mt-1">{subtitle}</div>}
      <ul className="mt-5 space-y-2 text-sm text-[#1c1917] flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={1.6} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        disabled={busy || disabled}
        data-testid={ctaTestId}
        className={`mt-6 h-11 rounded-[4px] tracking-wide ${highlight ? "bg-[#4e7b5b] hover:bg-[#3b6249] text-white" : "bg-[#1b3b36] hover:bg-[#2c5a53] text-white"}`}
      >
        {busy ? "Working…" : cta}
      </Button>
    </div>
  );
}

function ChangePasswordPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="max-w-md bg-white border border-[#e5e2d9] rounded-[4px] p-6 space-y-5" data-testid="change-password-form">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Security</div>
        <h2 className="font-display text-2xl text-[#1b3b36] mt-1">Change password</h2>
        <p className="text-sm text-[#57534e] mt-1">Pick something strong — at least 8 characters.</p>
      </div>
      <div>
        <Label htmlFor="cp-current" className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Current password</Label>
        <Input id="cp-current" data-testid="cp-current-input" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-2 h-11 bg-white border-[#e5e2d9]" />
      </div>
      <div>
        <Label htmlFor="cp-new" className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">New password</Label>
        <Input id="cp-new" data-testid="cp-new-input" type="password" autoComplete="new-password" minLength={8} required value={next} onChange={(e) => setNext(e.target.value)} className="mt-2 h-11 bg-white border-[#e5e2d9]" />
      </div>
      <div>
        <Label htmlFor="cp-confirm" className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Confirm new password</Label>
        <Input id="cp-confirm" data-testid="cp-confirm-input" type="password" autoComplete="new-password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 h-11 bg-white border-[#e5e2d9]" />
      </div>
      <Button type="submit" data-testid="cp-submit-button" disabled={busy} className="h-11 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] tracking-wide">
        {busy ? "Saving…" : "Update password"}
      </Button>
    </form>
  );
}

function AccountPanel({ user, refresh }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPwd, setDelPwd] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const isOwner = user?.role === "owner";

  const load = async () => {
    try {
      const { data } = await api.get("/account/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load account");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.info(text);
    }
  };

  const exportData = () => {
    // Direct browser navigation lets the auth cookie attach and the browser handle the file save.
    const a = document.createElement("a");
    a.href = `${API_BASE}/account/export`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Your export will download shortly");
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    if ((delConfirm || "").trim() !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/account/delete", { password: delPwd, confirm_text: delConfirm });
      toast.success(`Workspace scheduled for deletion in ${data.grace_days} days. You can cancel any time.`);
      setDelOpen(false);
      setDelPwd(""); setDelConfirm("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not request deletion");
    } finally {
      setBusy(false);
    }
  };

  const cancelDelete = async () => {
    setBusy(true);
    try {
      await api.post("/account/delete/cancel");
      toast.success("Deletion cancelled — your workspace is safe.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to cancel deletion");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#8a8476]" data-testid="account-loading">Loading account…</div>;

  return (
    <div className="space-y-6" data-testid="account-panel">
      {/* Pending deletion banner */}
      {status?.deletion_scheduled_for && (
        <div className="bg-[#fbeae8] border border-[#f1c5be] text-[#a23a30] rounded-[4px] p-4 flex items-start gap-3" data-testid="account-pending-deletion">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
          <div className="flex-1">
            <div className="font-medium text-sm">This workspace is scheduled for deletion</div>
            <div className="text-xs mt-1">Every record will be permanently removed on <b>{new Date(status.deletion_scheduled_for).toLocaleString()}</b>. You can still cancel before then.</div>
          </div>
          <Button size="sm" variant="outline" onClick={cancelDelete} disabled={busy} data-testid="account-cancel-deletion" className="border-[#a23a30] text-[#a23a30] hover:bg-[#fbeae8]">
            Cancel deletion
          </Button>
        </div>
      )}

      {/* Referrals card */}
      <div className="bg-white border border-[#e5e2d9] rounded-[4px] p-6" data-testid="referrals-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-[3px] bg-[#fbeae2] text-[#c0612f] flex items-center justify-center shrink-0">
            <Gift className="w-4 h-4" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Refer a kitchen</div>
            <h3 className="font-display text-2xl text-[#1b3b36] mt-1">Earn a free month, on us</h3>
            <p className="text-sm text-[#57534e] mt-2 leading-relaxed">
              When another kitchen signs up with your code <em>and</em> subscribes, you both get <b>1 free month</b> credited automatically. No cap.
            </p>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <div className="border border-[#e5e2d9] rounded-[3px] p-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">Your code</div>
                <div className="flex items-center gap-2 mt-1">
                  <code data-testid="referrals-code" className="font-mono text-lg text-[#1b3b36] font-medium">{status?.referral_code || "—"}</code>
                  <button type="button" onClick={() => copyCode(status?.referral_code)} aria-label="Copy code" className="p-1 text-[#8a8476] hover:text-[#1b3b36]" data-testid="referrals-copy-code">
                    <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              </div>
              <div className="border border-[#e5e2d9] rounded-[3px] p-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">Share link</div>
                <div className="flex items-center gap-2 mt-1">
                  <span data-testid="referrals-link" className="text-xs text-[#1b3b36] truncate font-mono">{status?.referral_url || "—"}</span>
                  <button type="button" onClick={() => copyCode(status?.referral_url)} aria-label="Copy link" className="p-1 text-[#8a8476] hover:text-[#1b3b36] shrink-0" data-testid="referrals-copy-link">
                    <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div className="border border-[#e5e2d9] rounded-[3px] p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8476]">Invites</div>
                <div className="font-display text-2xl text-[#1b3b36] mt-1" data-testid="referrals-total">{status?.referrals_total ?? 0}</div>
              </div>
              <div className="border border-[#e5e2d9] rounded-[3px] p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8476]">Subscribed</div>
                <div className="font-display text-2xl text-[#1b3b36] mt-1" data-testid="referrals-converted">{status?.referrals_converted ?? 0}</div>
              </div>
              <div className="border border-[#e5e2d9] rounded-[3px] p-3 bg-[#e8f0e8]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#4e7b5b]">Free months banked</div>
                <div className="font-display text-2xl text-[#4e7b5b] mt-1" data-testid="referrals-credits">
                  {Math.max(0, (status?.referral_credits_months ?? 0) - (status?.referral_credit_used_months ?? 0))}
                </div>
                {!!status?.referral_credit_used_months && (
                  <div className="text-[10px] text-[#4e7b5b]/80 mt-1" data-testid="referrals-redeemed">
                    {status.referral_credit_used_months} already redeemed
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export */}
      <div className="bg-white border border-[#e5e2d9] rounded-[4px] p-6 flex items-start gap-4" data-testid="export-card">
        <div className="w-9 h-9 rounded-[3px] bg-[#efece6] text-[#1b3b36] flex items-center justify-center shrink-0">
          <Download className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl text-[#1b3b36]">Export my data</h3>
          <p className="text-sm text-[#57534e] mt-1">Download a ZIP archive containing JSON + CSV of every record we hold for this workspace.</p>
          <Button onClick={exportData} disabled={!isOwner} data-testid="export-download-button" className="mt-3 h-10 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.8} />
            Download ZIP
          </Button>
          {!isOwner && <div className="text-xs text-[#8a8476] mt-2">Only the workspace owner can export.</div>}
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white border border-[#f1c5be] rounded-[4px] p-6 flex items-start gap-4" data-testid="delete-card">
        <div className="w-9 h-9 rounded-[3px] bg-[#fbeae8] text-[#a23a30] flex items-center justify-center shrink-0">
          <Trash2 className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl text-[#a23a30]">Delete workspace</h3>
          <p className="text-sm text-[#57534e] mt-1">
            Permanently removes <b>{user?.business_name}</b> and every record inside it — audits, batches, invoices, staff, attachments — and every user attached to this workspace. We hold it for 30 days first; you can cancel until then.
          </p>
          <Dialog open={delOpen} onOpenChange={setDelOpen}>
            <DialogTrigger asChild>
              <Button disabled={!isOwner || !!status?.deletion_scheduled_for} data-testid="delete-open-dialog" className="mt-3 h-10 bg-[#a23a30] hover:bg-[#8a2f27] text-white rounded-[4px]">
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {status?.deletion_scheduled_for ? "Already scheduled" : "Delete this workspace"}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="delete-dialog" className="max-w-md">
              <DialogHeader>
                <DialogTitle>Delete <i>{user?.business_name}</i>?</DialogTitle>
                <DialogDescription>
                  This will permanently remove the workspace and every record in 30 days. You can cancel any time before then.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitDelete} className="space-y-4 mt-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Your password</Label>
                  <Input data-testid="delete-password-input" type="password" required autoComplete="current-password" value={delPwd} onChange={(e) => setDelPwd(e.target.value)} className="mt-2 h-11 bg-white border-[#e5e2d9]" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Type DELETE to confirm</Label>
                  <Input data-testid="delete-confirm-input" required value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="DELETE" className="mt-2 h-11 bg-white border-[#e5e2d9] font-mono uppercase" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDelOpen(false)} disabled={busy}>Cancel</Button>
                  <Button type="submit" disabled={busy} data-testid="delete-submit-button" className="bg-[#a23a30] hover:bg-[#8a2f27] text-white">
                    {busy ? "Scheduling…" : "Delete workspace"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="text-xs text-[#8a8476] flex items-center gap-4 pt-2">
        <a href="/legal/terms" className="hover:underline">Terms</a>
        <a href="/legal/privacy" className="hover:underline">Privacy</a>
        <a href="/legal/cookies" className="hover:underline">Cookies</a>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("session_id") || params.get("mock") || params.get("cancel") || params.get("portal") ? "billing" : (params.get("tab") || "billing");
  const [tab, setTab] = useState(initialTab);

  const onTabChange = (t) => {
    setTab(t);
    setParams({ tab: t }, { replace: true });
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1500px]" data-testid="settings-page">
      <PageHeader
        eyebrow="Account"
        title="Settings"
        description="Manage your subscription, security and workspace preferences."
        testid="settings-header"
      />
      <div className="max-w-5xl">
        <Tabs value={tab} onValueChange={onTabChange} data-testid="settings-tabs">
          <TabsList className="bg-[#efece6] border border-[#e5e2d9]">
            <TabsTrigger value="billing" data-testid="tab-billing" className="data-[state=active]:bg-white data-[state=active]:text-[#1b3b36]">
              <CreditCard className="w-3.5 h-3.5 mr-2" strokeWidth={1.6} /> Billing
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="data-[state=active]:bg-white data-[state=active]:text-[#1b3b36]">
              <KeyRound className="w-3.5 h-3.5 mr-2" strokeWidth={1.6} /> Security
            </TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account" className="data-[state=active]:bg-white data-[state=active]:text-[#1b3b36]">
              <UserCog className="w-3.5 h-3.5 mr-2" strokeWidth={1.6} /> Account
            </TabsTrigger>
            {user?.role === "owner" && (
              <TabsTrigger value="health" data-testid="tab-health" className="data-[state=active]:bg-white data-[state=active]:text-[#1b3b36]">
                <Activity className="w-3.5 h-3.5 mr-2" strokeWidth={1.6} /> Health
              </TabsTrigger>
            )}
            {user?.role === "owner" && (
              <TabsTrigger value="signups" data-testid="tab-signups" className="data-[state=active]:bg-white data-[state=active]:text-[#1b3b36]">
                <Users className="w-3.5 h-3.5 mr-2" strokeWidth={1.6} /> Sign-ups
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="billing" className="mt-6">
            <BillingPanel user={user} refresh={refresh} />
          </TabsContent>

          <TabsContent value="security" className="mt-6">
            <ChangePasswordPanel />
          </TabsContent>

          <TabsContent value="account" className="mt-6">
            <AccountPanel user={user} refresh={refresh} />
          </TabsContent>

          {user?.role === "owner" && (
            <TabsContent value="health" className="mt-6">
              <HealthPanel />
            </TabsContent>
          )}

          {user?.role === "owner" && (
            <TabsContent value="signups" className="mt-6">
              <SignupsPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HealthPanel — owner-only production-readiness dashboard.
// Wraps GET /api/admin/readiness so owners never need to hit the raw JSON URL.
// ---------------------------------------------------------------------------
function HealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/readiness");
      setData(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load readiness checks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const statusColor = data?.status === "ready"
    ? "text-[#4e7b5b] bg-[#e8f0e8]"
    : "text-[#b9531c] bg-[#fbeae8]";

  return (
    <div className="max-w-3xl" data-testid="health-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#1b3b36]">Production readiness</h2>
          <p className="text-sm text-[#57534e] mt-1">Live status of the 10 P0/P1 checks that keep Hearth healthy for your customers.</p>
        </div>
        <Button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="health-refresh"
          variant="outline"
          className="rounded-[4px]"
        >
          <RefreshCcw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
          {loading ? "Checking…" : "Re-check"}
        </Button>
      </div>

      {error && (
        <div data-testid="health-error" className="text-sm text-[#d94f3d] bg-[#fbeae8] px-3 py-2 rounded-[3px] mb-4">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-[3px] mb-6 ${statusColor}`} data-testid="health-status">
            {data.status === "ready" ? (
              <CheckCircle2 className="w-5 h-5" strokeWidth={1.8} />
            ) : (
              <AlertTriangle className="w-5 h-5" strokeWidth={1.8} />
            )}
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em]">Status</div>
              <div className="font-mono text-lg leading-tight">{String(data.status).toUpperCase()}</div>
            </div>
            <div className="border-l border-current/20 pl-3 ml-2 text-xs opacity-80">
              {data.total} check{data.total === 1 ? "" : "s"} · P0 failing: {data.p0_failing} · P1 failing: {data.p1_failing}
            </div>
          </div>

          <div className="border border-[#e5e2d9] rounded-[3px] divide-y divide-[#e5e2d9]" data-testid="health-checklist">
            {(data.checks || []).map((c, i) => (
              <div
                key={i}
                data-testid={`health-check-${i}`}
                className="flex items-start gap-4 px-4 py-3.5 bg-white"
              >
                <div className="mt-0.5">
                  {c.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-[#4e7b5b]" strokeWidth={2} />
                  ) : (
                    <AlertTriangle className={`w-4 h-4 ${c.severity === "p0" ? "text-[#d94f3d]" : "text-[#b9531c]"}`} strokeWidth={2} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1b3b36] text-sm">{c.name}</span>
                    <span className={`text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded ${c.severity === "p0" ? "bg-[#fbeae8] text-[#d94f3d]" : "bg-[#fdf3e6] text-[#b9531c]"}`}>
                      {c.severity}
                    </span>
                  </div>
                  <div className="text-xs text-[#57534e] mt-1 leading-relaxed break-words">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-[11px] text-[#8a8476] mt-4 leading-relaxed">
            This page wraps <code className="font-mono">GET /api/admin/readiness</code>. If any check goes red, fix the underlying env var or config in your deploy environment and re-check here.
          </div>
        </>
      )}

      {!data && !error && loading && (
        <div className="text-sm text-[#8a8476]">Loading readiness checks…</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// SignupsPanel — platform-admin-only. Lists every workspace + owner + activity.
// The API endpoint checks the caller's email against OWNER_ACCOUNT_EMAIL server-side;
// non-admin owners will see the "Not authorised" state.
// ---------------------------------------------------------------------------
function SignupsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [filter, setFilter] = useState("all"); // all | paying | engaged | empty
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    setForbidden(false);
    try {
      const res = await api.get("/admin/signups");
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load sign-ups");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const purgeEmpty = async () => {
    const emptyCount = data?.summary?.empty ?? 0;
    if (emptyCount === 0) {
      toast.info("Nothing to purge — no empty workspaces.");
      return;
    }
    const ok = window.confirm(
      `Hard-delete ${emptyCount} empty workspace${emptyCount === 1 ? "" : "s"}? ` +
      `This will remove each workspace + its owner user + all cascaded rows. Cannot be undone.`
    );
    if (!ok) return;
    setPurging(true);
    setError("");
    try {
      const res = await api.post("/admin/empty-workspaces/purge", { confirm: "DELETE" });
      toast.success(`Purged ${res.data.purged_count} empty workspace${res.data.purged_count === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to purge empty workspaces");
    } finally {
      setPurging(false);
    }
  };

  if (forbidden) {
    return (
      <div className="max-w-2xl" data-testid="signups-forbidden">
        <div className="bg-[#fdf3e6] border border-[#f1e0c0] rounded-[3px] px-4 py-3 text-sm text-[#7a3d12] flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5" strokeWidth={1.8} />
          <div>
            <div className="font-medium">Platform-admin only</div>
            <div className="text-xs mt-1">Only the platform owner ({/* redacted for privacy */}the account that runs Hearth) can see this list. If that's you, sign in as your platform-admin account.</div>
          </div>
        </div>
      </div>
    );
  }

  const rows = data?.signups || [];
  const filtered = filter === "all" ? rows : rows.filter((r) => r.activity_level === filter);
  const summary = data?.summary || {};

  const badge = (level) => {
    const map = {
      paying: "bg-[#e8f0e8] text-[#4e7b5b]",
      engaged: "bg-[#fdf3e6] text-[#b9531c]",
      empty: "bg-[#f4f2ed] text-[#8a8476]",
    };
    return map[level] || map.empty;
  };

  const filterPill = (value, label, count) => (
    <button
      type="button"
      key={value}
      data-testid={`signups-filter-${value}`}
      onClick={() => setFilter(value)}
      className={`text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border transition-colors ${
        filter === value
          ? "bg-[#1b3b36] text-white border-[#1b3b36]"
          : "bg-white text-[#57534e] border-[#e5e2d9] hover:border-[#1b3b36]"
      }`}
    >
      {label} <span className="font-mono ml-1 opacity-70">{count}</span>
    </button>
  );

  return (
    <div className="max-w-5xl" data-testid="signups-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#1b3b36]">Sign-ups</h2>
          <p className="text-sm text-[#57534e] mt-1">Everyone who's signed up to Hearth. Sorted newest first. Only visible to the platform admin.</p>
        </div>
        <div className="flex items-center gap-2">
          {(data?.summary?.empty ?? 0) > 0 && (
            <Button
              type="button"
              onClick={purgeEmpty}
              disabled={purging || loading}
              data-testid="signups-purge-empty"
              variant="outline"
              className="rounded-[4px] border-[#d94f3d] text-[#d94f3d] hover:bg-[#fbeae8] hover:text-[#d94f3d]"
            >
              <Trash2 className={`w-3.5 h-3.5 mr-2 ${purging ? "animate-pulse" : ""}`} strokeWidth={1.8} />
              {purging ? "Purging…" : `Purge ${data.summary.empty} empty`}
            </Button>
          )}
          <Button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="signups-refresh"
            variant="outline"
            className="rounded-[4px]"
          >
            <RefreshCcw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {error && (
        <div data-testid="signups-error" className="text-sm text-[#d94f3d] bg-[#fbeae8] px-3 py-2 rounded-[3px] mb-4">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="signups-summary">
            {[
              { label: "Total", value: summary.total, key: "total" },
              { label: "Paying", value: summary.paying, key: "paying" },
              { label: "Engaged", value: summary.engaged, key: "engaged" },
              { label: "Empty", value: summary.empty, key: "empty" },
              { label: "Pending delete", value: summary.pending_deletion, key: "deletion" },
            ].map((s) => (
              <div key={s.key} className="border border-[#e5e2d9] rounded-[3px] bg-white px-3 py-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">{s.label}</div>
                <div className="font-display text-2xl text-[#1b3b36] mt-1">{s.value ?? 0}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {filterPill("all", "All", summary.total ?? 0)}
            {filterPill("paying", "Paying", summary.paying ?? 0)}
            {filterPill("engaged", "Engaged", summary.engaged ?? 0)}
            {filterPill("empty", "Empty", summary.empty ?? 0)}
          </div>

          <div className="border border-[#e5e2d9] rounded-[3px] overflow-x-auto" data-testid="signups-table-wrap">
            <table className="w-full text-sm" data-testid="signups-table">
              <thead className="bg-[#f7f5f0] text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">
                <tr>
                  <th className="text-left px-3 py-2.5">Business</th>
                  <th className="text-left px-3 py-2.5">Owner</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Activity</th>
                  <th className="text-left px-3 py-2.5">Items</th>
                  <th className="text-left px-3 py-2.5">Signed up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e2d9]">
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[#8a8476] text-xs">No sign-ups match this filter.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.business_id} data-testid={`signups-row-${r.business_id}`} className="bg-white hover:bg-[#faf9f5]">
                    <td className="px-3 py-2.5 align-top">
                      <div className="font-medium text-[#1b3b36]">{r.business_name || "—"}</div>
                      {r.deletion_scheduled_for && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d94f3d] mt-1">Pending delete</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="text-[#1c1917]">{r.owner_name || "—"}</div>
                      <div className="text-xs text-[#57534e] font-mono">{r.owner_email}</div>
                      {!r.email_verified && r.owner_email && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#b9531c] mt-1">Unverified email</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className="text-xs font-mono">{r.subscription_status || "—"}</span>
                      {r.complimentary && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4e7b5b] mt-1">Complimentary</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full ${badge(r.activity_level)}`}>
                        {r.activity_level}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className="font-mono text-xs">{r.total_items}</span>
                      {r.total_items > 0 && (
                        <div className="text-[10px] text-[#8a8476] mt-1">
                          {r.counts.fridge_logs > 0 && <span>{r.counts.fridge_logs}F </span>}
                          {r.counts.audits > 0 && <span>{r.counts.audits}A </span>}
                          {r.counts.events > 0 && <span>{r.counts.events}E </span>}
                          {r.counts.invoices > 0 && <span>{r.counts.invoices}I </span>}
                          {r.counts.batches > 0 && <span>{r.counts.batches}B </span>}
                          {r.counts.training > 0 && <span>{r.counts.training}T </span>}
                          {r.counts.staff > 0 && <span>{r.counts.staff}S</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className="text-xs text-[#57534e]">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-[#8a8476] mt-3">
            Legend: F=fridge logs · A=audits · E=events · I=invoices · B=batches · T=training · S=staff.
            &nbsp;<b>Empty</b> = zero records across all modules. <b>Engaged</b> = has records but not paying yet. <b>Paying</b> = active subscription or complimentary.
          </div>
        </>
      )}

      {!data && !error && !forbidden && loading && (
        <div className="text-sm text-[#8a8476]">Loading sign-ups…</div>
      )}
    </div>
  );
}
